import React, { useState, useEffect } from 'react';
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  LinearProgress,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  TextField,
  Typography,
} from '@mui/material';
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import WarningIcon from '@mui/icons-material/Warning';
import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord';
import { useTranslation } from 'react-i18next';

/** Silinemeyen bir cari ve backend'in döndürdüğü gerekçe */
export interface BulkDeleteFailure {
  name: string;
  reason: string;
}

/** Toplu silme sonrası özet — tüm cariler tek tek işlendikten sonra doldurulur */
export interface BulkDeleteResult {
  succeededCount: number;
  failures: BulkDeleteFailure[];
}

interface BulkDeleteAccountsDialogProps {
  open: boolean;
  selectedCount: number;
  deleting: boolean;
  /** Kaç carinin işlendiği (ilerleme çubuğu için) */
  processedCount: number;
  /** İşlem bittiğinde dolar; null ise onay aşamasındayız */
  result: BulkDeleteResult | null;
  onConfirm: () => void;
  onCancel: () => void;
}

const REQUIRED_TEXT = 'ONAYLIYORUM';

const BulkDeleteAccountsDialog: React.FC<BulkDeleteAccountsDialogProps> = ({
  open,
  selectedCount,
  deleting,
  processedCount,
  result,
  onConfirm,
  onCancel,
}) => {
  const { t } = useTranslation('accounting');
  const [confirmationText, setConfirmationText] = useState<string>('');

  const isConfirmed = confirmationText.trim().toUpperCase() === REQUIRED_TEXT;

  useEffect(() => {
    if (!open) {
      setConfirmationText('');
    }
  }, [open]);

  const handleConfirm = () => {
    if (isConfirmed) {
      onConfirm();
      setConfirmationText('');
    }
  };

  const progressValue = selectedCount > 0 ? (processedCount / selectedCount) * 100 : 0;

  return (
    <Dialog open={open} onClose={deleting ? undefined : onCancel} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, color: 'error.main' }}>
        <WarningIcon />
        {t('current.bulkDelete.title')}
      </DialogTitle>

      <DialogContent>
        {/* ─── Sonuç aşaması ─── */}
        {result ? (
          <>
            <Alert severity={result.succeededCount > 0 ? 'success' : 'warning'} sx={{ mb: 2 }}>
              <AlertTitle sx={{ fontWeight: 'bold' }}>{t('current.bulkDelete.resultTitle')}</AlertTitle>
              {t('current.bulkDelete.resultSucceeded', { count: result.succeededCount })}
              {result.failures.length > 0 &&
                ` — ${t('current.bulkDelete.resultFailed', { count: result.failures.length })}`}
            </Alert>

            {result.failures.length > 0 && (
              <Box>
                <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                  {t('current.bulkDelete.resultFailedHeading')}
                </Typography>
                <List dense disablePadding>
                  {result.failures.map((failure, index) => (
                    <ListItem key={`${failure.name}-${index}`} disableGutters sx={{ py: 0.25 }}>
                      <ListItemIcon sx={{ minWidth: 26 }}>
                        <FiberManualRecordIcon sx={{ fontSize: 10 }} color="error" />
                      </ListItemIcon>
                      <ListItemText
                        primary={failure.name}
                        secondary={failure.reason}
                        primaryTypographyProps={{ variant: 'body2', fontWeight: 'medium' }}
                        secondaryTypographyProps={{ variant: 'caption' }}
                      />
                    </ListItem>
                  ))}
                </List>
              </Box>
            )}
          </>
        ) : deleting ? (
          /* ─── İlerleme aşaması ─── */
          <Box sx={{ py: 2 }}>
            <Typography variant="body2" sx={{ mb: 1.5 }}>
              {t('current.bulkDelete.progress', { current: processedCount, total: selectedCount })}
            </Typography>
            <LinearProgress variant="determinate" value={progressValue} color="error" />
          </Box>
        ) : (
          /* ─── Onay aşaması ─── */
          <>
            <Alert severity="error" sx={{ mb: 2 }}>
              <Typography variant="body2" fontWeight="bold">
                {t('current.bulkDelete.warningTitle')}
              </Typography>
            </Alert>

            <DialogContentText sx={{ mb: 2 }}>
              {t('current.bulkDelete.description')}{' '}
              <Box component="span" sx={{ fontWeight: 'bold', color: 'error.main' }}>
                {t('current.bulkDelete.countUnit', { count: selectedCount })}
              </Box>
            </DialogContentText>

            <Box
              sx={{
                mt: 2,
                p: 2,
                bgcolor: 'grey.50',
                border: '1px solid',
                borderColor: 'grey.300',
                borderRadius: 1,
              }}
            >
              <Typography variant="subtitle2" gutterBottom fontWeight="bold">
                {t('current.bulkDelete.detailsHeading')}
              </Typography>
              <Typography variant="body2" sx={{ mt: 1 }}>
                • {t('current.bulkDelete.detailTotal', { count: selectedCount })}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                • {t('current.bulkDelete.detailBlocked')}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                • {t('current.bulkDelete.detailSystem')}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                • {t('current.bulkDelete.detailIrreversible')}
              </Typography>
            </Box>

            <Box sx={{ mt: 3 }}>
              <Typography variant="body2" fontWeight="bold" color="error.main" gutterBottom>
                {t('current.bulkDelete.confirmPrompt')}
              </Typography>
              <TextField
                fullWidth
                autoFocus
                variant="outlined"
                placeholder={t('current.bulkDelete.confirmPlaceholder')}
                value={confirmationText}
                onChange={(e) => setConfirmationText(e.target.value)}
                sx={{ mt: 1 }}
                error={confirmationText.length > 0 && !isConfirmed}
                helperText={
                  confirmationText.length > 0 && !isConfirmed
                    ? t('current.bulkDelete.confirmError')
                    : isConfirmed
                    ? t('current.bulkDelete.confirmOk')
                    : ''
                }
                inputProps={{
                  style: { textTransform: 'uppercase', letterSpacing: '1px', fontWeight: 'bold' },
                }}
              />
            </Box>
          </>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onCancel} color="primary" disabled={deleting} variant="outlined">
          {result ? t('current.bulkDelete.close') : t('current.actions.cancel')}
        </Button>
        {!result && (
          <Button
            onClick={handleConfirm}
            color="error"
            variant="contained"
            disabled={deleting || !isConfirmed}
            startIcon={deleting ? <CircularProgress size={20} color="inherit" /> : <DeleteForeverIcon />}
          >
            {deleting
              ? t('current.bulkDelete.deleting')
              : t('current.bulkDelete.confirmButton', { count: selectedCount })}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default BulkDeleteAccountsDialog;
